"use client";

import { datadogRum } from "@datadog/browser-rum";

/**
 * ActionName type enforces the structural format for analytics action names.
 * THIS IS A WORK IN PROGRESS IF THIS DOES NOT WORK FOR YOUR USECASE SEND A MESSAGE
 * TO THE #team-prod-eng channel
 *
 * Format: `feature:element:action`
 *
 * Examples:
 * - `appointment:scheduled:completed`
 * - `calendar_filters:gender:click`
 * - `booking_form:submit:success`
 *
 * Why template literal types instead of strict union types?
 * - We use compositional action names that can have hundreds of combinations
 * - A strict union type would require constant updates for every new tracking point
 * - The template literal type `${string}:${string}:${string}` enforces the structure
 *   at compile time while maintaining flexibility for new tracking
 *
 * What this catches:
 * - Missing sections (e.g., `feature:action` - missing element)
 * - Wrong number of colons (e.g., `feature:element` or `feature:element:action:extra`)
 * - Structural mistakes that would break analytics parsing
 *
 * What this doesn't catch:
 * - Typos within sections (e.g., `cal_com:appointmnt:scheduled` - typo in "appointment")
 * - Incorrect feature/element/action names (e.g., `cal_com:wrong_element:click`)
 *
 * Rationale:
 * - At scale, one-off typos won't significantly impact analytics
 * - Data can be normalized after collection if needed
 * - The structural enforcement prevents the most common mistakes (missing sections)
 * - Balance between type safety and developer ergonomics
 */
type ActionName = `${string}:${string}:${string}`;

/**
 * Track a custom action in Datadog RUM.
 * Only sends the action if Datadog is configured (NEXT_PUBLIC_DD_CLIENT_TOKEN is set).
 *
 * @param actionName - The name of the action to track. Must follow the format `feature:element:action`.
 *                     Examples: `appointment:scheduled:completed`, `calendar_filters:gender:click`
 * @param actionContext - Additional context/metadata for the action
 */

export function trackDataDogAction(
  actionName: ActionName,
  actionContext?: Record<string, unknown>,
): void {
  if (process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
    datadogRum.addAction(actionName, actionContext);
  }
}

/**
 * Start a named Datadog RUM view for funnel and Product Analytics tracking.
 * Only sends if Datadog is configured (NEXT_PUBLIC_DD_CLIENT_TOKEN is set).
 *
 * @param name - The view name (e.g. `onboarding/welcome`). Appears in Product Analytics funnels.
 */
export function startDataDogView(name: string): void {
  if (process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN) {
    datadogRum.startView({ name });
  }
}
